import React, { useEffect, useState } from 'react';

// Define asset boundaries
const ASSET_MIN_DATE = new Date('2022-01-01'); // Minimum date for historical data

// Helper: Compute the default deforestation range (last 3 years from current date)
const getDefaultDeforestationRange = () => {
  const today = new Date();
  const threeYearsBefore = new Date();
  threeYearsBefore.setFullYear(today.getFullYear() - 3);
  const clampedStart = threeYearsBefore < ASSET_MIN_DATE ? ASSET_MIN_DATE : threeYearsBefore;
  return {
    startDate: clampedStart.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
};

// Helper to clamp a given date range to asset boundaries
const clampDateRange = (range) => {
  let { startDate, endDate } = range;
  let s = new Date(startDate);
  let e = new Date(endDate);
  const requestedDuration = e.getTime() - s.getTime();

  if (s < ASSET_MIN_DATE) {
    s = ASSET_MIN_DATE;
    e = new Date(s.getTime() + requestedDuration);
  }

  const today = new Date();
  if (e > today) {
    e = today;
    s = new Date(e.getTime() - requestedDuration);
  }

  if (s < ASSET_MIN_DATE) s = ASSET_MIN_DATE;

  return {
    startDate: s.toISOString().split("T")[0],
    endDate: e.toISOString().split("T")[0]
  };
};

const DeforestationComponent = ({
  layerConfig,
  mapInstance,
  dateRange = getDefaultDeforestationRange(),
  onLoad,
}) => {
  const [localDateRange, setLocalDateRange] = useState(dateRange);

  // Reset handling effect
  useEffect(() => {
    if (layerConfig?.deforestationRange) {
      const clampedRange = validateAndClampDateRange(layerConfig.deforestationRange);
      setLocalDateRange(clampedRange);

      if (mapInstance && layerConfig.isVisible !== undefined) {
        ['low', 'high', 'highest'].forEach(confidenceLevel => {
          const layerId = `deforestation-${confidenceLevel}-confidence`;
          if (mapInstance.getLayer(layerId)) {
            mapInstance.setLayoutProperty(
              layerId,
              'visibility',
              layerConfig.isVisible ? 'visible' : 'none'
            );
          }
        });
      }

      if (onLoad) {
        onLoad({
          ...layerConfig,
          dateRange: clampedRange
        });
      }
    }
  }, [layerConfig?.lastReset, layerConfig?.deforestationRange, mapInstance, onLoad]);

  // Helper to remove layer/source from the map
  const cleanupLayer = (layerId) => {
    if (!mapInstance) return;
    if (mapInstance.getLayer(layerId)) {
      mapInstance.removeLayer(layerId);
    }
    if (mapInstance.getSource(layerId)) {
      mapInstance.removeSource(layerId);
    }
  };

  // Validate and clamp date range
  const validateAndClampDateRange = (range) => {
    const clamped = clampDateRange(range);
    if (new Date(clamped.startDate) > new Date(clamped.endDate)) {
      throw new Error(`Invalid date range after clamping: startDate=${clamped.startDate}, endDate=${clamped.endDate}`);
    }
    return clamped;
  };

  // Helper to build the tile URL for a specific confidence level
  const buildTileUrl = (confidenceLevel, dates) => {
    const baseUrl = `https://tiles.globalforestwatch.org/gfw_integrated_alerts/latest/dynamic/{z}/{x}/{y}.png`;
    const params = new URLSearchParams({
      start_date: dates.startDate,
      end_date: dates.endDate,
      render_type: "true_color",
      alert_confidence: confidenceLevel
    });
    return `${baseUrl}?${params.toString()}`;
  };

  // Define the confidence layers without any visual effects to preserve original colors
  const getConfidenceLayers = () => [
    {
      id: "deforestation-low-confidence",
      confidence: "low",
      paint: {
        "raster-opacity": 1.0
      }
    },
    {
      id: "deforestation-high-confidence",
      confidence: "high",
      paint: {
        "raster-opacity": 1.0
      }
    },
    {
      id: "deforestation-highest-confidence",
      confidence: "highest",
      paint: {
        "raster-opacity": 1.0
      }
    }
  ];

  // Helper to setup all confidence layers without removing them during updates
  const setupConfidenceLayers = async () => {
    if (!mapInstance) return;

    try {
      const effectiveDateRange = validateAndClampDateRange(localDateRange);
      const confidenceLayers = getConfidenceLayers();

      // Setup each confidence layer
      for (const layer of confidenceLayers) {
        const tileUrl = buildTileUrl(layer.confidence, effectiveDateRange);

        // If the layer already exists, just update its source
        if (mapInstance.getLayer(layer.id)) {
          // If source exists, update tiles
          if (mapInstance.getSource(layer.id)) {
            mapInstance.getSource(layer.id).setTiles([tileUrl]);
          }
          // Update paint properties if needed
          Object.entries(layer.paint).forEach(([property, value]) => {
            mapInstance.setPaintProperty(layer.id, property, value);
          });
        } else {
          // Layer doesn't exist, create it
          if (!mapInstance.getSource(layer.id)) {
            mapInstance.addSource(layer.id, {
              type: "raster",
              tiles: [tileUrl],
              tileSize: 256,
              maxzoom: 12,
              minzoom: 3
            });
          }

          mapInstance.addLayer({
            id: layer.id,
            type: "raster",
            source: layer.id,
            layout: {
              visibility: layerConfig.isVisible ? 'visible' : 'none',
            },
            paint: layer.paint
          });
        }
      }

      if (onLoad) {
        onLoad({
          ...layerConfig,
          dateRange: effectiveDateRange,
        });
      }
    } catch (err) {
      console.error(`Deforestation: failed to setup layers`, err);
    }
  };

  // Set up layers when the component mounts or when dependencies change
  useEffect(() => {
    if (!mapInstance) return;

    // Set up layers initially when the map loads
    const setupInitially = () => {
      setupConfidenceLayers();
    };

    if (mapInstance.isStyleLoaded()) {
      setupInitially();
    } else {
      mapInstance.once("load", setupInitially);
    }

    // Clean up on unmount
    return () => {
      getConfidenceLayers().forEach(layer => cleanupLayer(layer.id));
    };
  }, [mapInstance, localDateRange.startDate, localDateRange.endDate]);

  // Update opacity
  const updateOpacity = (newOpacity) => {
    if (!mapInstance) return;
    const layers = getConfidenceLayers();
    const normalizedOpacity = Math.min(1, newOpacity / 100);

    layers.forEach(layer => {
      if (mapInstance.getLayer(layer.id)) {
        mapInstance.setPaintProperty(layer.id, 'raster-opacity', normalizedOpacity);
      }
    });

    console.log(`Updated opacity for deforestation layers to ${normalizedOpacity}`);
  };

  useEffect(() => {
    if (!mapInstance || layerConfig?.opacity == null) return;
    updateOpacity(layerConfig.opacity);
  }, [mapInstance, layerConfig?.opacity]);

  // Update localDateRange when prop changes
  useEffect(() => {
    if (dateRange) {
      setLocalDateRange(dateRange);
    }
  }, [dateRange]);

  return null; // No UI component rendered
};

export default DeforestationComponent;
